import { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '../../api/client';
import { fmtLocalDate } from '../../utils/datetime';
import { FormTitle } from '../../components/FormTitle';
import './executive.css';

/* ── Types ─────────────────────────────────────────────────────── */

interface DetailCol { key: string; label: string; isDate?: boolean; highlight?: 'jobid' | 'overdue' }
interface DetailCfg { title: string; icon: string; url: string; cols: DetailCol[] }

type DetailRow = Record<string, unknown>;

const JOB_COLS: DetailCol[] = [
  { key: 'jobId',        label: 'Job ID',        highlight: 'jobid' },
  { key: 'client',       label: 'Client'  },
  { key: 'projectId',    label: 'Project' },
  { key: 'receivedDate', label: 'Received', isDate: true },
  { key: 'dueDate',      label: 'Due Date', isDate: true, highlight: 'overdue' },
  { key: 'statusName',   label: 'Status'  },
];

const TEST_COLS: DetailCol[] = [
  { key: 'jobId',       label: 'Job ID',    highlight: 'jobid' },
  { key: 'sampleId',    label: 'Sample ID' },
  { key: 'testName',    label: 'Test'      },
  { key: 'method',      label: 'Method'    },
  { key: 'analyzedDate',label: 'Analyzed', isDate: true },
  { key: 'analyzedBy',  label: 'By'        },
];

// Render via the shared §14b.6 helper (local tz, MM/DD/YYYY); keep the '—'
// placeholder for empty values used in the detail-modal date columns.
const fmtDate = (v: unknown) => {
  if (!v) return '—';
  return fmtLocalDate(v as string) || String(v);
};

/* ── Detail Modal ───────────────────────────────────────────────── */
function DetailModal({ cfg, onClose }: { cfg: DetailCfg; onClose: () => void }) {
  const [rows,    setRows]    = useState<DetailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState<string | null>(null);

  useEffect(() => {
    setLoading(true); setErr(null); setRows([]);
    api.get(cfg.url)
      .then(r => setRows((r.data?.data ?? []) as DetailRow[]))
      .catch(() => setErr('Failed to load detail data.'))
      .finally(() => setLoading(false));
  }, [cfg.url]);

  const today = new Date(); today.setHours(0,0,0,0);

  return (
    <div className="exec-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="exec-modal">
        <div className="exec-modal-hdr">
          <div className="exec-modal-title">
            <i className={`ti ${cfg.icon}`} />
            {cfg.title}
            {!loading && <span className="exec-modal-count">{rows.length} record{rows.length !== 1 ? 's' : ''}</span>}
          </div>
          <button className="exec-modal-close" onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <div className="exec-modal-body">
          {loading && <div className="exec-modal-loading"><i className="ti ti-loader" /> Loading…</div>}
          {err     && <div className="exec-modal-loading" style={{ color:'#c45a08' }}>{err}</div>}
          {!loading && !err && rows.length === 0 && <div className="exec-modal-empty">No records found.</div>}
          {!loading && !err && rows.length > 0 && (
            <table className="exec-detail-tbl">
              <thead>
                <tr>{cfg.cols.map(c => <th key={c.key}>{c.label}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i}>
                    {cfg.cols.map(c => {
                      const val = row[c.key];
                      const display = c.isDate ? fmtDate(val) : (val == null || val === '' ? '—' : String(val));
                      let cls = '';
                      if (c.highlight === 'jobid') cls = 'jobid';
                      else if (c.highlight === 'overdue' && val) {
                        const d = new Date(val as string); d.setHours(0,0,0,0);
                        if (d < today) cls = 'overdue';
                      }
                      return <td key={c.key} className={cls}>{display}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Types ──────────────────────────────────────────────────── */
interface Kpis {
  // Job-level (Samplecheckin, MailStatus-based)
  openJobs: number; overdue: number; dueToday: number;
  totalJobs1m: number; reportsDelivered7d: number;
  samplesDue3dTests: number; samplesDue3dSamples: number; samplesDue3dClients: number;
  // Test-level workflow stages
  pendingEntry: number; pendingSubmission: number; pendingValidation: number;
  pendingResultApproval: number; pendingReporting: number;
  // SDMS batch-level
  sdmsPendingReview: number; sdmsValidated: number;
  // Totals
  totalSamples: number; totalTests: number;
  activeClients: number; activeProjects: number; qcBatches: number;
}
interface DailyCount { date: string; count: number; }
interface NamedCount { name: string; count: number; }

const fmt = (n: number | undefined) => n == null ? '—' : n.toLocaleString();

/* Build the donut CSS conic-gradient from job category counts */
function buildDonut(kpis: Kpis) {
  const total = Math.max(kpis.openJobs, 1);
  const overdueN   = Math.min(kpis.overdue, total);
  // "Submitted" ≈ jobs in final reporting stage
  const submittedN = Math.min(kpis.pendingReporting, total);
  // "Pending Login" ≈ jobs due today not yet fully entered
  const pendingN   = Math.min(kpis.dueToday, total - overdueN - submittedN);
  // "In Progress" ≈ everything else actively being processed
  const inProgN    = Math.max(0, total - overdueN - submittedN - pendingN);

  const s  = submittedN / total;          // green
  const ip = (submittedN + inProgN) / total;  // yellow
  const ov = (submittedN + inProgN + overdueN) / total; // red

  const p = (v: number) => `${(v * 100).toFixed(2)}%`;

  const gradient = `conic-gradient(
    #5DCAA5 0% ${p(s)},
    #f5c518 ${p(s)} ${p(ip)},
    #ff7d6e ${p(ip)} ${p(ov)},
    #ccc    ${p(ov)} 100%
  )`;

  return {
    gradient,
    segments: [
      { label: 'In Reporting', val: submittedN, pct: Math.round(s * 100), color: '#5DCAA5' },
      { label: 'In Progress',  val: inProgN,    pct: Math.round((ip - s) * 100), color: '#f5c518' },
      { label: 'Overdue',      val: overdueN,   pct: Math.round((ov - ip) * 100), color: '#ff7d6e' },
      { label: 'Due Today',    val: pendingN,   pct: Math.round((1 - ov) * 100), color: '#ccc' },
    ],
  };
}

/* Build SVG polyline points from daily count array */
function buildSparkPoints(data: DailyCount[]): { line: string; area: string } {
  if (data.length < 2) return { line: '', area: '' };
  const vals = data.map((d) => d.count);
  const maxV = Math.max(...vals, 1);
  const minV = Math.min(...vals);
  const range = maxV - minV || 1;
  const n = data.length;
  const pts = data.map((d, i) => {
    const x = ((i / (n - 1)) * 400).toFixed(1);
    const y = (10 + ((maxV - d.count) / range) * 50).toFixed(1);
    return `${x},${y}`;
  });
  const line = pts.join(' ');
  const area = `${pts.join(' ')} 400,70 0,70`;
  return { line, area };
}

/* Generate alert messages from KPI data */
function buildAlerts(kpis: Kpis) {
  const alerts: Array<{ kind: '' | 'warn' | 'info' | 'ok'; icon: string; title: string; sub: string; time: string }> = [];
  if (kpis.overdue > 0) {
    alerts.push({
      kind: '', icon: 'ti-alert-octagon',
      title: `${kpis.overdue} job${kpis.overdue > 1 ? 's' : ''} overdue`,
      sub: 'Past due date — immediate attention required',
      time: 'now',
    });
  }
  if (kpis.sdmsPendingReview > 0) {
    alerts.push({
      kind: 'warn', icon: 'ti-clock-exclamation',
      title: `${fmt(kpis.sdmsPendingReview)} SDMS results awaiting validation`,
      sub: `Pending SDMS validation queue · ${kpis.sdmsPendingReview} items`,
      time: 'today',
    });
  }
  if (kpis.dueToday > 0) {
    alerts.push({
      kind: 'warn', icon: 'ti-alert-triangle',
      title: `${fmt(kpis.dueToday)} samples due today`,
      sub: 'Check the due samples backlog',
      time: 'today',
    });
  }
  if (kpis.pendingResultApproval > 0) {
    alerts.push({
      kind: 'info', icon: 'ti-info-circle',
      title: `${fmt(kpis.pendingResultApproval)} tests pending result approval`,
      sub: 'Reviewer sign-off required',
      time: 'today',
    });
  }
  if (alerts.length === 0 || kpis.sdmsValidated > 0) {
    alerts.push({
      kind: 'ok', icon: 'ti-circle-check',
      title: `${fmt(kpis.sdmsValidated)} SDMS results validated`,
      sub: 'Validated and ready for reporting',
      time: 'today',
    });
  }
  return alerts;
}

/* ── Component ───────────────────────────────────────────────── */
export function ExecutiveDashboardPage({ onHome }: { onHome?: () => void }) {
  const [kpis,    setKpis]    = useState<Kpis | null>(null);
  const [trend,   setTrend]   = useState<DailyCount[]>([]);
  const [clients, setClients] = useState<NamedCount[]>([]);
  const [busy,    setBusy]    = useState(true);
  const [loadMs,  setLoadMs]  = useState<number | null>(null);
  const [err,     setErr]     = useState<string | null>(null);
  const [detail,  setDetail]  = useState<DetailCfg | null>(null);

  const openDetail = useCallback((cfg: DetailCfg) => setDetail(cfg), []);
  const closeDetail = useCallback(() => setDetail(null), []);

  const now = new Date();
  /* calendar-header label — permitted §18.6 exception (hero eyebrow month-name
     heading, not a data value) */
  const dateLabel = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const refresh = () => {
    setBusy(true); setErr(null);
    const t0 = performance.now();
    Promise.all([
      api.get('/api/v1/dashboard/kpis'),
      api.get('/api/v1/dashboard/samples-per-day', { params: { days: 14 } }),
      api.get('/api/v1/dashboard/top-clients',     { params: { days: 90, take: 7 } }),
    ])
      .then(([k, s, c]) => {
        setKpis(k.data?.data ?? null);
        setTrend((s.data?.data ?? []) as DailyCount[]);
        setClients((c.data?.data ?? []) as NamedCount[]);
      })
      .catch(() => setErr('Could not load dashboard data.'))
      .finally(() => { setLoadMs(performance.now() - t0); setBusy(false); });
  };
  useEffect(refresh, []);

  /* Derived values — computed once kpis arrive */
  const donut     = useMemo(() => (kpis ? buildDonut(kpis)      : null), [kpis]);
  const spark     = useMemo(() => buildSparkPoints(trend),               [trend]);
  const alerts    = useMemo(() => (kpis ? buildAlerts(kpis)     : []),   [kpis]);

  /* Pipeline stages — ordered 00–06 */
  const pipelineMax = useMemo(() => {
    if (!kpis) return 1;
    return Math.max(
      kpis.dueToday, kpis.pendingEntry, kpis.pendingSubmission,
      kpis.sdmsPendingReview, kpis.pendingValidation,
      kpis.pendingResultApproval, kpis.pendingReporting, 1,
    );
  }, [kpis]);

  const pipeline = kpis ? [
    { label: '00 — Due Samples',       count: kpis.dueToday,              color: '#e8a014' },
    { label: '01 — Pending Entry',     count: kpis.pendingEntry,          color: '#e8a014' },
    { label: '02 — Pending Submission',count: kpis.pendingSubmission,     color: '#185FA5' },
    { label: '03 — SDMS Result Entry', count: kpis.sdmsPendingReview,     color: '#185FA5' },
    { label: '04 — Data Validation',   count: kpis.pendingValidation,     color: '#534AB7' },
    { label: '05 — Result Approval',   count: kpis.pendingResultApproval, color: '#534AB7' },
    { label: '06 — Reporting',         count: kpis.pendingReporting,      color: '#0F6E56' },
  ] : [];

  /* Top clients total for percentage */
  const clientTotal = useMemo(() => clients.reduce((s, c) => s + c.count, 0) || 1, [clients]);

  /* Hero headline text */
  const heroTitle = useMemo(() => {
    if (!kpis) return 'Loading lab overview…';
    const qc = kpis.qcBatches > 0
      ? `${kpis.qcBatches} QC batches active`
      : 'Lab overview';
    return kpis.overdue > 0
      ? `${qc} · ${kpis.overdue} job${kpis.overdue > 1 ? 's' : ''} overdue`
      : `${qc} — all jobs on track`;
  }, [kpis]);

  /* Last trend count for "vs last week" delta */
  const trendDelta = useMemo(() => {
    if (trend.length < 8) return null;
    const thisWeek = trend.slice(-7).reduce((s, d) => s + d.count, 0);
    const lastWeek = trend.slice(-14, -7).reduce((s, d) => s + d.count, 0);
    return thisWeek - lastWeek;
  }, [trend]);

  return (
    <div className="exec-page">

      {/* ── Top bar ── */}
      <div className="dp-topbar">
        <div className="dp-topbar-row">
          <div>
            <div className="dp-breadcrumb">
              {onHome ? <a onClick={onHome}>Home</a> : null}
              {onHome ? <i className="ti ti-chevron-right" /> : null}
              <span>Executive Dashboard</span>
            </div>
            {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour
                via navMeta('execdash') + the TOP load-time chip. */}
            <FormTitle pageId="execdash" loadMs={loadMs} loading={busy} />
            <div className="dp-sub">
              Live overview · As of {fmtLocalDate(now.toISOString())} ·{' '}
              <a onClick={refresh} style={{ pointerEvents: busy ? 'none' : 'auto' }}>
                {busy ? 'loading…' : 'Refresh'}
              </a>
            </div>
          </div>
          {/* Shared .ca-btn + prototype glyph (BO UI Standard §7) — read-only
              dashboard, so Refresh is the only action. */}
          <button className="ca-btn" onClick={refresh} disabled={busy}>
            <span className="tb-i tb-i-green">🔄</span> Refresh
          </button>
        </div>
      </div>

      {/* ── Scroll area ── */}
      <div className="dp-scroll">

        {err && <div className="exec-err">{err}</div>}

        {/* ── Hero banner ── */}
        <div className="exec-hero">
          <div className="exec-hero-left">
            <div className="exec-hero-ey">ALPACA Intelligence · {dateLabel}</div>
            <div className="exec-hero-t">{heroTitle}</div>
            <div className="exec-hero-s">
              {kpis
                ? `${fmt(kpis.activeClients)} active clients · ${fmt(kpis.activeProjects)} active projects · Read-only live mirror`
                : 'Live ALPACA database · Read-only mirror'}
            </div>
          </div>
          <div className="exec-hero-stats">
            <div className="exec-hero-stat exec-hero-stat-click" title="Click to view open jobs"
              onClick={() => openDetail({ title: 'Active (Open) Jobs', icon: 'ti-briefcase', url: '/api/v1/dashboard/detail/open-jobs', cols: JOB_COLS })}>
              <div className="exec-hero-stat-val">{fmt(kpis?.openJobs)}</div>
              <div className="exec-hero-stat-lbl">Active jobs</div>
            </div>
            <div className="exec-hero-div" />
            <div className="exec-hero-stat">
              <div className="exec-hero-stat-val g">{fmt(kpis?.qcBatches)}</div>
              <div className="exec-hero-stat-lbl">QC batches</div>
            </div>
            <div className="exec-hero-div" />
            <div className="exec-hero-stat">
              <div className="exec-hero-stat-val o">{fmt(kpis?.activeClients)}</div>
              <div className="exec-hero-stat-lbl">Active clients</div>
            </div>
            <div className="exec-hero-div" />
            <div className="exec-hero-stat exec-hero-stat-click" title="Click to view overdue jobs"
              onClick={() => openDetail({ title: 'Overdue Jobs', icon: 'ti-alert-octagon', url: '/api/v1/dashboard/detail/overdue', cols: JOB_COLS })}>
              <div className={`exec-hero-stat-val${(kpis?.overdue ?? 0) > 0 ? ' r' : ' g'}`}>
                {fmt(kpis?.overdue)}
              </div>
              <div className="exec-hero-stat-lbl">Overdue</div>
            </div>
          </div>
        </div>

        {/* ── 4 KPI tiles — labels match v5.html exactly ── */}
        <div className="exec-kpi-row">

          {/* Tile 1 — Total Jobs (1M) — blue bar */}
          <div className="exec-kpi exec-kpi-clickable"
            onClick={() => openDetail({ title: 'Total Jobs — Last 30 Days', icon: 'ti-briefcase', url: '/api/v1/dashboard/detail/recent-jobs?days=30', cols: JOB_COLS })}>
            <div className="exec-kpi-bar" />
            <div className="exec-kpi-lbl">Total Jobs (1M)</div>
            <div className="exec-kpi-val">{fmt(kpis?.totalJobs1m)}</div>
            {trendDelta != null ? (
              <div className={`exec-kpi-trend ${trendDelta >= 0 ? 'up' : 'down'}`}>
                <i className={`ti ${trendDelta >= 0 ? 'ti-trending-up' : 'ti-trending-down'}`} />
                {trendDelta >= 0 ? '+' : ''}{trendDelta} vs last week
              </div>
            ) : (
              <div className="exec-kpi-trend flat">—</div>
            )}
          </div>

          {/* Tile 2 — Samples Due (3d) — red bar — format: Tests / Samples / Clients */}
          <div className="exec-kpi exec-kpi-clickable"
            onClick={() => openDetail({ title: 'Samples Due — Next 3 Days', icon: 'ti-clock-exclamation', url: '/api/v1/dashboard/detail/samples-due?days=3', cols: JOB_COLS })}>
            <div className="exec-kpi-bar r" />
            <div className="exec-kpi-lbl">Samples Due (3d)</div>
            <div className="exec-kpi-val">
              {kpis
                ? `${fmt(kpis.samplesDue3dTests)} / ${fmt(kpis.samplesDue3dSamples)} / ${fmt(kpis.samplesDue3dClients)}`
                : '—'}
            </div>
            <div className="exec-kpi-sub">Tests / Samples / Clients</div>
          </div>

          {/* Tile 3 — Pending Validation — amber bar */}
          <div className="exec-kpi exec-kpi-clickable"
            onClick={() => openDetail({ title: 'Pending Validation (Data Review)', icon: 'ti-check', url: '/api/v1/dashboard/detail/pending-validation', cols: TEST_COLS })}>
            <div className="exec-kpi-bar a" />
            <div className="exec-kpi-lbl">Pending Validation</div>
            <div className="exec-kpi-val">{fmt(kpis?.pendingValidation)}</div>
            {(kpis?.sdmsPendingReview ?? 0) > 0 ? (
              <div className="exec-kpi-trend down">
                <i className="ti ti-trending-down" /> +{fmt(kpis?.sdmsPendingReview)} SDMS
              </div>
            ) : (
              <div className="exec-kpi-trend flat">SDMS clear</div>
            )}
          </div>

          {/* Tile 4 — Reports Delivered (7d) — green bar */}
          <div className="exec-kpi exec-kpi-clickable"
            onClick={() => openDetail({ title: 'Reports Delivered — Last 7 Days', icon: 'ti-file-check', url: '/api/v1/dashboard/detail/reports-delivered?days=7', cols: JOB_COLS })}>
            <div className="exec-kpi-bar g" />
            <div className="exec-kpi-lbl">Reports Delivered (7d)</div>
            <div className="exec-kpi-val">{fmt(kpis?.reportsDelivered7d)}</div>
            {(kpis?.reportsDelivered7d ?? 0) > 0 ? (
              <div className="exec-kpi-trend up">
                <i className="ti ti-trending-up" /> On-time delivery
              </div>
            ) : (
              <div className="exec-kpi-trend flat">No reports this week</div>
            )}
          </div>

        </div>

        {/* ── Pipeline + Donut ── */}
        <div className="exec-row r-2">

          {/* Sample Backlog Pipeline */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-stack" /> Sample Backlog Pipeline</div>
              <span style={{ fontSize: 10, color: 'var(--ai-muted)' }}>Click any stage to drill in</span>
            </div>
            <div className="exec-card-body">
              {pipeline.map((row) => {
                const widthPct = Math.round((row.count / pipelineMax) * 100);
                const displayPct = kpis ? Math.round((row.count / Math.max(kpis.totalSamples, 1)) * 100) : 0;
                return (
                  <div key={row.label} className="exec-pipe-row">
                    <div className="exec-pipe-lbl">{row.label}</div>
                    <div className="exec-pipe-cnt" style={{ color: row.color }}>{fmt(row.count)}</div>
                    <div className="exec-pipe-bar">
                      <div className="exec-pipe-fill" style={{ width: `${widthPct}%`, background: row.color }} />
                    </div>
                    <div className="exec-pipe-pct">{displayPct}%</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Job Status Donut */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-chart-pie" /> Job Status Distribution</div>
              <span style={{ fontSize: 10, color: 'var(--ai-muted)' }}>{fmt(kpis?.openJobs)} open jobs</span>
            </div>
            <div className="exec-card-body">
              <div className="exec-donut-wrap">
                <div
                  className="exec-donut"
                  style={{ background: donut?.gradient ?? 'conic-gradient(#ddd 0% 100%)' }}
                >
                  <div className="exec-donut-center">
                    <div className="exec-donut-center-val">{fmt(kpis?.openJobs)}</div>
                    <div className="exec-donut-center-lbl">Total</div>
                  </div>
                </div>
                <div className="exec-donut-legend">
                  {donut?.segments.map((seg) => (
                    <div key={seg.label} className="exec-donut-li">
                      <span className="exec-donut-dot" style={{ background: seg.color }} />
                      <span className="exec-donut-name">{seg.label}</span>
                      <span className="exec-donut-val">{fmt(seg.val)}</span>
                      <span className="exec-donut-pct">{seg.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* ── Top Clients + QC Sparkline ── */}
        <div className="exec-row r-12">

          {/* Top Clients */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-trophy" /> Top Clients by Volume</div>
              <span style={{ fontSize: 10, color: 'var(--ai-muted)' }}>Last 3 months</span>
            </div>
            <div className="exec-card-body">
              {clients.length === 0 && !busy ? (
                <div style={{ color: 'var(--ai-muted)', fontSize: 12, fontStyle: 'italic' }}>No jobs in this window.</div>
              ) : (
                <table className="exec-mini">
                  <tbody>
                    {clients.map((c, i) => (
                      <tr key={c.name + i}>
                        <td>{c.name}</td>
                        <td className="r">
                          {c.count}
                          <span className="pct">{Math.round((c.count / clientTotal) * 100)}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Daily Sample Trend Sparkline */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-chart-line" /> Samples Received · 14 days</div>
              <span style={{ fontSize: 10, color: '#0F6E56', fontWeight: 'bold' }}>
                {trend.length > 0 ? `${fmt(trend[trend.length - 1]?.count)} today` : '—'}
              </span>
            </div>
            <div className="exec-card-body">
              <div className="exec-spark-wrap">
                <svg className="exec-spark" viewBox="0 0 400 70" preserveAspectRatio="none">
                  {/* Area fill */}
                  {spark.area && (
                    <polyline points={spark.area} fill="#5DCAA5" opacity="0.18" />
                  )}
                  {/* Trend line */}
                  {spark.line && (
                    <polyline
                      points={spark.line}
                      fill="none"
                      stroke="#0F6E56"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  )}
                  {/* Data points */}
                  {trend.map((d, i) => {
                    const n = trend.length;
                    const maxV = Math.max(...trend.map((t) => t.count), 1);
                    const minV = Math.min(...trend.map((t) => t.count));
                    const range = maxV - minV || 1;
                    const x = (i / (n - 1)) * 400;
                    const y = 10 + ((maxV - d.count) / range) * 50;
                    return (
                      <circle
                        key={i}
                        cx={x.toFixed(1)}
                        cy={y.toFixed(1)}
                        r={i === n - 1 ? '2.5' : '2'}
                        fill="#0F6E56"
                        stroke={i === n - 1 ? '#fff' : 'none'}
                        strokeWidth="1"
                      />
                    );
                  })}
                </svg>
                <div className="exec-spark-x">
                  {trend.filter((_, i) => i % 2 === 0 || i === trend.length - 1).map((d, i) => (
                    <span key={i}>{d.date?.slice(5, 10)}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* ── Reporting Overview + Alerts ── */}
        <div className="exec-row r-2">

          {/* Reporting Overview */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-report-analytics" /> Reporting Overview</div>
            </div>
            <div className="exec-card-body">
              {(() => {
                const rows = kpis ? [
                  { label: 'Pending Result Approval',   count: kpis.pendingResultApproval, color: '#c45a08'  },
                  { label: 'SDMS Pending Review',        count: kpis.sdmsPendingReview,     color: '#e8a014'  },
                  { label: 'Pending Reporting (Jobs)',   count: kpis.pendingReporting,       color: '#e8a014'  },
                  { label: 'SDMS Validated',             count: kpis.sdmsValidated,          color: '#0F6E56'  },
                  { label: 'Pending Entry',              count: kpis.pendingEntry,           color: '#185FA5'  },
                ] : [];
                const max = Math.max(...rows.map((r) => r.count), 1);
                return rows.map((row) => (
                  <div key={row.label} className="exec-pipe-row" style={{ cursor: 'default' }}>
                    <div className="exec-pipe-lbl">{row.label}</div>
                    <div className="exec-pipe-cnt" style={{ color: row.color }}>{fmt(row.count)}</div>
                    <div className="exec-pipe-bar">
                      <div
                        className="exec-pipe-fill"
                        style={{ width: `${Math.round((row.count / max) * 100)}%`, background: row.color }}
                      />
                    </div>
                    <div className="exec-pipe-pct">{max > 1 ? Math.round((row.count / max) * 100) : 0}%</div>
                  </div>
                ));
              })()}
            </div>
          </div>

          {/* Alerts & Exceptions */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-bell" /> Alerts &amp; Exceptions</div>
              <span style={{ fontSize: 10, color: '#c45a08', fontWeight: 'bold' }}>
                {alerts.filter((a) => a.kind === '' || a.kind === 'warn').length} active
              </span>
            </div>
            <div className="exec-card-body">
              {alerts.map((a, i) => (
                <div key={i} className={`exec-alert${a.kind ? ` ${a.kind}` : ''}`}>
                  <i className={`ti ${a.icon}`} />
                  <div className="exec-alert-body">
                    <div className="exec-alert-t">{a.title}</div>
                    <div className="exec-alert-s">{a.sub}</div>
                  </div>
                  <span className="exec-alert-time">{a.time}</span>
                </div>
              ))}
              {alerts.length === 0 && !busy && (
                <div className="exec-alert ok">
                  <i className="ti ti-circle-check" />
                  <div className="exec-alert-body">
                    <div className="exec-alert-t">All systems normal</div>
                    <div className="exec-alert-s">No active alerts</div>
                  </div>
                  <span className="exec-alert-time">now</span>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* ── Bottom 3-column row ── */}
        <div className="exec-row r-3">

          {/* Workflow Stage Counts */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-list-check" /> Workflow Stages</div>
            </div>
            <div className="exec-card-body">
              {kpis && [
                { label: 'Pending Entry',          count: kpis.pendingEntry,          color: '#0ea5e9' },
                { label: 'Pending Submission',     count: kpis.pendingSubmission,     color: '#7c3aed' },
                { label: 'Pending Validation',     count: kpis.pendingValidation,     color: '#f59e0b' },
                { label: 'Result Approval',        count: kpis.pendingResultApproval, color: '#16a34a' },
                { label: 'Pending Reporting',      count: kpis.pendingReporting,      color: '#dc2626' },
                { label: 'SDMS Pending Review',    count: kpis.sdmsPendingReview,     color: '#e07b00' },
              ].map((row) => (
                <div key={row.label} className="exec-pipe-row no-bar" style={{ cursor: 'default' }}>
                  <div className="exec-pipe-lbl" style={{ minWidth: 'auto', flex: 1 }}>{row.label}</div>
                  <div className="exec-pipe-cnt" style={{ color: row.color }}>{fmt(row.count)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Lab Overview */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-building" /> Lab Overview</div>
            </div>
            <div className="exec-card-body">
              {kpis && [
                { label: 'Active Clients',    count: kpis.activeClients,  color: '#185FA5' },
                { label: 'Active Projects',   count: kpis.activeProjects, color: '#185FA5' },
                { label: 'Total Samples',     count: kpis.totalSamples,   color: '#0F6E56' },
                { label: 'Tests in Flow',     count: kpis.totalTests,     color: '#534AB7' },
                { label: 'QC Batches',        count: kpis.qcBatches,      color: '#e8a014' },
                { label: 'SDMS Validated',    count: kpis.sdmsValidated,  color: '#0F6E56' },
              ].map((row) => (
                <div key={row.label} className="exec-pipe-row no-bar" style={{ cursor: 'default' }}>
                  <div className="exec-pipe-lbl" style={{ minWidth: 'auto', flex: 1 }}>{row.label}</div>
                  <div className="exec-pipe-cnt" style={{ color: row.color }}>{fmt(row.count)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Clients Summary */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-trophy" /> Top Clients (90d)</div>
            </div>
            <div className="exec-card-body">
              <table className="exec-mini">
                <tbody>
                  {clients.slice(0, 6).map((c, i) => (
                    <tr key={i}>
                      <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.name}
                      </td>
                      <td className="r">
                        {c.count}
                        <span className="pct">{Math.round((c.count / clientTotal) * 100)}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="exec-footer">
          ALPACA Intelligence · Executive Dashboard · powered by BT·Soft &nbsp;·&nbsp; v26.8.12
        </div>

      </div>{/* dp-scroll */}

      {/* ── Detail Modal (portal-like, outside scroll area) ── */}
      {detail && <DetailModal cfg={detail} onClose={closeDetail} />}

    </div>
  );
}
